import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { LeadImportContent } from "component/shared/lead-import-content";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { User } from "model/user";
import { styles } from "./style";
import * as env from "shared/env";

interface Props extends WithStyles<typeof styles> {
	open: boolean;
	onClose: () => void;
	user: User;
}

const LeadImportModalComponent: React.FC<Props> = ({ open, onClose, user, classes = {} }) => {
	const actionsStateRef = React.useRef<{
		valid: boolean | undefined;
		submitted: boolean;
		inProgress: boolean;
		isStopping: boolean;
		progress: number;
		onImport: () => void;
		onCancel: () => void;
		onClear: () => void;
		onShowStopConfirm: () => void;
		onBypassImport: () => Promise<void>;
		importResultLength: number;
		renderCsvReader: (buttonLabel?: string) => React.ReactNode;
		hasImportPreview: boolean;
		validLeadsCount: number;
	} | null>(null);
	const resetOnOpenRef = React.useRef(false);

	const [, forceUpdate] = React.useReducer(x => x + 1, 0);

	const renderActions = React.useCallback((params: any) => {
		const prev = actionsStateRef.current;
		if (resetOnOpenRef.current && !params.inProgress) {
			resetOnOpenRef.current = false;
			params.onClear();
		}
		const hasChanged =
			!prev ||
			prev.valid !== params.valid ||
			prev.submitted !== params.submitted ||
			prev.inProgress !== params.inProgress ||
			prev.isStopping !== params.isStopping ||
			prev.progress !== params.progress ||
			prev.importResultLength !== params.importResultLength ||
			prev.hasImportPreview !== params.hasImportPreview ||
			prev.validLeadsCount !== params.validLeadsCount;
		actionsStateRef.current = params;
		if (hasChanged) {
			forceUpdate();
		}
		return null;
	}, []);

	React.useEffect(() => {
		if (!open) {
			actionsStateRef.current = null;
			resetOnOpenRef.current = false;
			forceUpdate();
		}
	}, [open]);

	React.useEffect(() => {
		if (open) {
			resetOnOpenRef.current = true;
		}
	}, [open]);

	return (
		<Mui.Dialog
			open={open}
			onClose={onClose}
			maxWidth={false}
			fullWidth
			PaperProps={{
				className: classes.leadImportDialogPaper,
			}}
		>
			<Mui.DialogTitle>
				<Mui.Grid container alignItems="center" justifyContent="space-between">
					<Mui.Grid item>
						<Mui.Grid container alignItems="center" spacing={1}>
							<Mui.Grid item>
								<Mui.Typography variant="h4" component="h2">
									Import Leads
								</Mui.Typography>
							</Mui.Grid>
						</Mui.Grid>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.IconButton onClick={onClose} size="small">
							<FeatherIcon>
								<Icons.X />
							</FeatherIcon>
						</Mui.IconButton>
					</Mui.Grid>
				</Mui.Grid>
			</Mui.DialogTitle>
			<Mui.DialogContent dividers>
				<LeadImportContent
					user={user}
					renderActions={renderActions}
					onImportComplete={onClose}
				/>
			</Mui.DialogContent>
			{actionsStateRef.current?.hasImportPreview && (
				<Mui.DialogActions className={classes.leadImportDialogActions}>
					{actionsStateRef.current.hasImportPreview && actionsStateRef.current.progress !== 100 && (
						<>
							<Mui.Button
								variant="outlined"
								onClick={() => {
									if (actionsStateRef.current?.inProgress) {
										actionsStateRef.current?.onShowStopConfirm();
									} else {
										actionsStateRef.current?.onCancel();
									}
								}}
								disabled={actionsStateRef.current?.isStopping}
								startIcon={actionsStateRef.current?.isStopping ? <LoadingIndicator size={20} /> : undefined}
							>
								{actionsStateRef.current?.isStopping
									? "Stopping... finishing current batch (up to 25)"
									: actionsStateRef.current?.inProgress
										? "Stop"
										: actionsStateRef.current.valid
											? "Cancel"
											: "Upload File Again"}
							</Mui.Button>
							{actionsStateRef.current.valid && (
								<Mui.Button
									variant="contained"
									color={"primary"}
									disabled={actionsStateRef.current?.inProgress}
									onClick={actionsStateRef.current.onImport}
								>
									Import Leads
								</Mui.Button>
							)}
						
							{!actionsStateRef.current.valid && !actionsStateRef.current.submitted && actionsStateRef.current.validLeadsCount > 0 && (
								<Mui.Button
									variant="contained"
									color={"primary"}
									onClick={actionsStateRef.current.onBypassImport}
									disabled={actionsStateRef.current.submitted}
								>
									Bypass Errors & Import
								</Mui.Button>
							)}
							{actionsStateRef.current?.isStopping && (
								<Mui.Typography variant="caption" style={{ marginLeft: 12 }}>
									Stopping after the current batch (up to 25 leads) finishes.
								</Mui.Typography>
							)}
						</>
					)}
					{actionsStateRef.current.progress === 100 && (
						<>
							<Mui.Button
								variant="contained"
								color="primary"
								onClick={() => {
									actionsStateRef.current?.onCancel();
									onClose();
								}}
							>
								Cancel
							</Mui.Button>
						</>
					)}
				</Mui.DialogActions>
			)}
		</Mui.Dialog>
	);
};

export const LeadImportModal = withStyles(LeadImportModalComponent, styles);
